import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { languageOptions } from '../types/i18n';
import { ui } from '../data/ui';
export function LanguageSwitcher() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const active = languageOptions.find(option => option.code === language) ?? languageOptions[0];
  const strings = ui[language];
  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);
  return <div ref={containerRef} className="relative" data-id="element-11" data-mp="23mv8d6bghff7">
      <button type="button" onClick={() => setOpen(value => !value)} aria-haspopup="listbox" aria-expanded={open} aria-label={strings.languageLabel} className="flex items-center gap-2 rounded-full border border-ink/20 bg-white/70 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-ink transition-colors hover:bg-white" data-id="element-12" data-mp="1ymz5xbiepw1r">
        <span aria-hidden="true" className="text-base leading-none" data-id="element-13" data-mp="2milft925r40h">
          {active.flag}
        </span>
        <span data-id="element-14" data-mp="3f18vua2arsup">{active.short}</span>
        <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-15" data-mp="3f2afh2xdpno1" />
      </button>

      <AnimatePresence data-id="element-16" data-mp="18cysmw8omz5p">
        {open && <motion.ul role="listbox" aria-label={strings.languageLabel} initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.18,
        ease: 'easeOut'
      }} className="absolute right-0 z-40 mt-2 w-48 border border-blush-300 bg-white py-1 shadow-lg" data-id="element-17" data-mp="18e9rqir6niqh">
            {languageOptions.map(option => {
          const selected = option.code === language;
          return <li key={option.code} role="option" aria-selected={selected} data-id="element-18" data-mp="tlzmrsz8eopt">
                  <button type="button" onClick={() => {
              setLanguage(option.code);
              setOpen(false);
            }} className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors hover:bg-blush-100 ${selected ? 'font-semibold text-ink' : 'text-ink/70'}`} data-id="element-19" data-mp="fpvwgarhb0im">
                    <span aria-hidden="true" className="text-base leading-none" data-id="element-20" data-mp="2k6smuh4sehx3">
                      {option.flag}
                    </span>
                    <span className="flex-1" data-id="element-21" data-mp="2xz8ll5qnnts7">{option.label}</span>
                    {selected && <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-22" data-mp="2t5w3zfke8n8y" />}
                  </button>
                </li>;
        })}
          </motion.ul>}
      </AnimatePresence>
    </div>;
}